import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { formatCurrency } from 'utils/format';
import { PriceRequest, PriceRequestLine, PriceRequestLineOutcome } from 'models/price-request';
import { DeltaCell, DeltaPercentCell, HistoryButton, ProductCell } from './price-request-line-cells.component';

type PriceRequestLinesTableProps = {
    request: PriceRequest;
    onOpenHistory: (line: PriceRequestLine) => void;
};

const days = (value: number | null | undefined) => (value === null || value === undefined ? '–' : String(value));

/** A proposed value only counts when the lab set it; an untouched field keeps the current one. */
const ifSet = (value: number | null | undefined, format: (value: number) => string) =>
    value === null || value === undefined ? undefined : format(value);

/** "current → proposed" when the lab proposed a different value, else just the current one. */
const Values: FC<{ current: string; proposed?: string; isChanged: boolean }> = ({ current, proposed, isChanged }) =>
    isChanged && proposed !== undefined && proposed !== current ? (
        <>
            <Typography variant="body2" component="span" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                {current}
            </Typography>{' '}
            <Typography variant="body2" component="span" fontWeight={600}>
                {proposed}
            </Typography>
        </>
    ) : (
        <>{current}</>
    );

/**
 * DD-3697: the request's lines as saved — current vs proposed values, the price delta in
 * currency and percent (computed by the backend), and the lab's answer per line.
 */
const PriceRequestLinesTable: FC<PriceRequestLinesTableProps> = ({ request, onOpenHistory }) => {
    const { t: translate } = useTranslation('price-requests');
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const money = (value: number | null | undefined) => formatCurrency(value, request.currency) || '–';

    return (
        <TableContainer component={Paper}>
            <Table size={isLargeScreen ? 'medium' : 'small'} data-test="price-request.lines">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('PRODUCT')}</TableCell>
                        <TableCell align="right">{translate('PRICE')}</TableCell>
                        <TableCell align="right">{translate('CHANGE')}</TableCell>
                        <TableCell align="right">{translate('CHANGE_PERCENT')}</TableCell>
                        <TableCell align="right">{translate('TIOTANDVARD_PRICE')}</TableCell>
                        <TableCell align="right">{translate('DELIVERY_DAYS')}</TableCell>
                        <TableCell>{translate('ANSWER')}</TableCell>
                        <TableCell padding="checkbox" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {request.lines.map((line) => {
                        const isChanged = line.outcome === PriceRequestLineOutcome.CHANGED;
                        return (
                            <TableRow key={line._id} data-test={`price-request.line-${line.action}`}>
                                <TableCell>
                                    <ProductCell line={line} />
                                </TableCell>
                                <TableCell align="right">
                                    <Values
                                        current={money(line.current.price)}
                                        proposed={ifSet(line.proposed?.price, money)}
                                        isChanged={isChanged}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <DeltaCell delta={line.priceDelta} currency={request.currency} />
                                </TableCell>
                                <TableCell align="right">
                                    <DeltaPercentCell delta={line.priceDelta} percent={line.pricePercent} />
                                </TableCell>
                                <TableCell align="right">
                                    <Values
                                        current={money(line.current.tiotandvardPrice)}
                                        proposed={ifSet(line.proposed?.tiotandvardPrice, money)}
                                        isChanged={isChanged}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Values
                                        current={days(line.current.deliveryTime)}
                                        proposed={ifSet(line.proposed?.deliveryTime, days)}
                                        isChanged={isChanged}
                                    />
                                </TableCell>
                                <TableCell>{translate(`OUTCOME_${line.outcome}`)}</TableCell>
                                <TableCell padding="checkbox">
                                    <HistoryButton line={line} onOpen={onOpenHistory} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PriceRequestLinesTable;
